import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Loader2, Trash2, UserPlus, Users, Zap } from "lucide-react";
import { Seo } from "../components/Seo";
import { listMembers, createMember, deleteMember, errorMessage } from "../lib/api";
import { usePro } from "../context/ProContext";

export default function EquipePage() {
    const { isBusiness, role, checking } = usePro();
    const allowed = isBusiness && role === "owner";
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [label, setLabel] = useState("");
    const [creating, setCreating] = useState(false);
    const [newToken, setNewToken] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = useCallback(() => {
        listMembers()
            .then(setData)
            .catch((e) => setError(errorMessage(e)));
    }, []);

    useEffect(() => {
        if (allowed) load();
    }, [allowed, load]);

    const add = async (e) => {
        e.preventDefault();
        if (!label.trim() || creating) return;
        setCreating(true);
        setError(null);
        try {
            const res = await createMember(label.trim());
            setNewToken(res.token);
            setCopied(false);
            setLabel("");
            load();
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (id) => {
        try {
            await deleteMember(id);
            load();
        } catch (e) {
            setError(errorMessage(e));
        }
    };

    const copyToken = async () => {
        try {
            await navigator.clipboard.writeText(newToken);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <main className="py-14 px-6 max-w-3xl mx-auto min-h-[60vh]">
            <Seo title="Minha equipe — ScorpionPDF Business" description="Gerencie os usuários da sua assinatura Business." noindex />
            <span className="font-mono-tag text-xs font-bold text-[#0284C7] uppercase">Plano Business</span>
            <h1 className="font-poster text-5xl md:text-6xl text-black mt-1">Minha equipe</h1>

            {checking ? (
                <p className="mt-10 font-mono-tag text-sm text-gray-600 flex items-center gap-2" data-testid="team-checking">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Verificando sua assinatura…
                </p>
            ) : !allowed ? (
                <div className="mt-10 border-2 border-black bg-black text-white p-10 text-center hard-shadow" data-testid="team-business-required">
                    <Zap className="w-10 h-10 text-[#22D3EE] mx-auto mb-4" aria-hidden="true" />
                    <p className="font-poster text-2xl">RECURSO DO PLANO BUSINESS</p>
                    <p className="text-sm text-gray-300 mt-2 max-w-md mx-auto">
                        Com o Business, até 5 pessoas usam o ScorpionPDF com a mesma assinatura — cada uma com seus
                        limites e histórico privado.
                    </p>
                    <Link
                        to="/planos"
                        data-testid="team-plans-link"
                        className="inline-block mt-6 px-8 py-4 bg-[#0284C7] hover:bg-[#22D3EE] text-white font-poster text-xl tracking-wide transition-colors"
                    >
                        CONHECER O BUSINESS
                    </Link>
                </div>
            ) : (
                <>
                    <p className="text-gray-700 mt-4">
                        Sua assinatura cobre <strong>{data ? `${data.used} de ${data.seats}` : "…"} usuários</strong> (você
                        incluso). Gere um código de acesso para cada pessoa — ela ativa em{" "}
                        <Link to="/planos" className="underline">Planos → “Tem um código de acesso?”</Link>.
                    </p>

                    {error && (
                        <p className="mt-4 border-2 border-black bg-[#FEF2F2] p-3 text-sm text-red-800" role="alert" data-testid="team-error">{error}</p>
                    )}

                    {/* novo membro */}
                    <form onSubmit={add} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="team-add-form">
                        <input
                            type="text"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Nome do membro (ex.: Ana — Financeiro)"
                            aria-label="Nome do membro"
                            maxLength={60}
                            data-testid="team-member-label"
                            className="flex-1 border-2 border-black bg-white px-3 py-2.5 text-sm font-medium"
                        />
                        <button
                            type="submit"
                            disabled={creating || !label.trim()}
                            data-testid="team-add-button"
                            className="px-6 py-2.5 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 text-white font-mono-tag text-xs font-bold uppercase transition-colors inline-flex items-center justify-center gap-2"
                        >
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <UserPlus className="w-4 h-4" aria-hidden="true" />}
                            Gerar acesso
                        </button>
                    </form>

                    {/* token recem-criado: exibido uma unica vez */}
                    {newToken && (
                        <div className="mt-4 border-2 border-black bg-[#F0F9FF] p-4" data-testid="team-new-token-box">
                            <p className="font-bold text-sm text-black">Código criado — copie e envie ao membro agora:</p>
                            <p className="mt-1 text-xs text-gray-600">
                                Por segurança, ele não aparece completo novamente. Se perder, remova e gere outro.
                            </p>
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <code className="flex-1 min-w-0 break-all border-2 border-black bg-white px-3 py-2 text-xs font-mono-tag" data-testid="team-new-token">
                                    {newToken}
                                </code>
                                <button
                                    onClick={copyToken}
                                    data-testid="team-copy-token"
                                    className="px-4 py-2 bg-black text-[#22D3EE] font-mono-tag text-xs font-bold uppercase hover:bg-[#0284C7] hover:text-white transition-colors inline-flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" aria-hidden="true" /> {copied ? "Copiado!" : "Copiar"}
                                </button>
                                <button
                                    onClick={() => setNewToken(null)}
                                    data-testid="team-dismiss-token"
                                    className="px-4 py-2 border-2 border-black bg-white font-mono-tag text-xs font-bold uppercase hover:bg-black hover:text-white transition-colors"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* membros */}
                    {data === null ? (
                        <p className="mt-8 font-mono-tag text-sm text-gray-600 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Carregando…
                        </p>
                    ) : data.members.length === 0 ? (
                        <div className="mt-8 border-2 border-black bg-white p-10 text-center" data-testid="team-empty">
                            <Users className="w-10 h-10 text-gray-400 mx-auto mb-3" aria-hidden="true" />
                            <p className="font-poster text-2xl text-black">SÓ VOCÊ POR ENQUANTO</p>
                            <p className="text-sm text-gray-600 mt-2">Gere o primeiro código de acesso acima.</p>
                        </div>
                    ) : (
                        <ul className="mt-8 space-y-3" data-testid="team-member-list">
                            {data.members.map((m) => (
                                <li key={m.id} className="border-2 border-black bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`team-member-${m.id}`}>
                                    <Users className="w-5 h-5 text-[#0284C7] flex-shrink-0" aria-hidden="true" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm text-black truncate">{m.label}</p>
                                        <p className="font-mono-tag text-[11px] text-gray-500">
                                            código {m.token_preview} · desde {new Date(m.created_at).toLocaleDateString("pt-BR")}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => revoke(m.id)}
                                        data-testid={`team-revoke-${m.id}`}
                                        aria-label={`Remover ${m.label}`}
                                        className="p-2.5 border-2 border-black bg-white hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </main>
    );
}
